'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { CalendarDays, ArrowRight } from 'lucide-react';
import { getZodiacMeta, getZodiacSlugFromBirthDate } from '@/lib/horoscope-utils';

export default function HoroscopeSignFinder() {
  const router = useRouter();
  const [birthDate, setBirthDate] = useState('');
  const [touched, setTouched] = useState(false);

  const signMeta = useMemo(() => {
    const slug = getZodiacSlugFromBirthDate(birthDate);
    return slug ? getZodiacMeta(slug) : null;
  }, [birthDate]);

  const helperText = !birthDate
    ? 'Enter your birth date and we will route you to the right sign page.'
    : signMeta
      ? `That birth date maps to ${signMeta.name} ${signMeta.symbol}.`
      : 'Use a valid birth date to find your sign.';

  return (
    <div className="rounded-[1.75rem] border border-starlight/10 bg-[linear-gradient(180deg,rgba(31,26,43,0.82),rgba(17,14,26,0.94))] p-6 md:p-7">
      <div className="max-w-2xl">
        <p className="text-sm uppercase tracking-[0.22em] text-stardust-gold/60">
          Find my sign
        </p>
        <h2 className="mt-2 text-3xl font-playfair text-stardust-gold">
          Know the birth date, not the zodiac label?
        </h2>
        <p className="mt-3 text-sm leading-7 text-starlight/74">
          Start with birth date now. You can still move into a fuller chart reading after this.
        </p>
      </div>

      <form
        className="mt-5 flex flex-col gap-3 md:flex-row"
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);

          if (!signMeta) {
            return;
          }

          router.push(`/horoscope/${signMeta.slug}`);
        }}
      >
        <label className="relative flex-1">
          <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-starlight/46" />
          <input
            type="date"
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
            className="w-full rounded-xl border border-starlight/12 bg-black/18 py-3 pl-11 pr-4 text-sm text-starlight outline-none transition-colors focus:border-stardust-gold/34"
          />
        </label>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#8b6914] px-5 py-3 text-sm font-semibold text-[#1b160f] transition-all hover:brightness-110"
        >
          Open my sign guide
          <ArrowRight size={16} />
        </button>
      </form>

      <p
        className={`mt-3 text-sm leading-6 ${
          touched && !signMeta ? 'text-rose-300' : 'text-starlight/62'
        }`}
      >
        {helperText}
      </p>
    </div>
  );
}
